import { useNavigate } from "@solidjs/router";
import { useQueryClient } from "@tanstack/solid-query";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { getApiErrorMessage } from "@/api/utils/error";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { applySessionBootstrapToAuthStore } from "@/domains/auth/session/apply-session-bootstrap";
import { useSelectWorkspaceMutation } from "../api";
import type { CompanyV2 } from "../api/types";
import { useCompanyActions, useCompanyStore } from "../store/company-store";
import { confirmLeavingUnsavedWork } from "../store/unsaved-work";

/**
 * Switches the active workspace and persists the choice on the backend
 * (`POST /company/:companyId/select`) so `/session/bootstrap` returns it as
 * `selected_workspace` after a reload.
 *
 * On success we apply the fresh lifecycle context to the auth store, commit the
 * new workspace, send the user to the company home and reset the entire query
 * cache so no data scoped to the previous workspace survives the switch (even
 * queries not keyed by `companyId`). `resetQueries` (not `clear`) is used on
 * purpose: it reverts queries to their initial state *and* refetches the active
 * ones, so the destination page reloads instead of hanging on its skeleton.
 * State is only mutated after the backend confirms, so a failed switch leaves
 * the current workspace untouched and just surfaces the error.
 */
export const useSelectWorkspace = () => {
  const [companyStore] = useCompanyStore();
  const [, setAuthStoreV2] = useAuthV2();
  const { selectCompany } = useCompanyActions();
  const selectWorkspaceMutation = useSelectWorkspaceMutation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useI18n();

  const selectWorkspace = async (company: CompanyV2) => {
    if (company.id === companyStore.selectedCompany?.id) {
      return;
    }

    // Asked **before** the mutation, not on the route. Everything below this
    // line is irreversible from the page's point of view — the store commits,
    // the backend records the choice and the cache is wiped — so a guard that
    // only blocked `navigate()` would leave a page mounted on a company that is
    // no longer the selected one.
    if (!(await confirmLeavingUnsavedWork())) {
      return;
    }

    try {
      const response = await selectWorkspaceMutation.mutateAsync(company.id);
      applySessionBootstrapToAuthStore(setAuthStoreV2, response.context);
      selectCompany(response.context.selected_workspace ?? response.workspace);
      navigate("/profile/company");
      queryClient.resetQueries();
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          t("company.sidebar.companySelector.select_error"),
          t,
        ),
      );
    }
  };

  return {
    selectWorkspace,
    isPending: () => selectWorkspaceMutation.isPending,
  };
};
